import { Injectable } from "@angular/core"
import { AngularFirestoreCollection, AngularFirestoreDocument } from "@angular/fire/firestore"
import * as firebase from "firebase/app"

import { Bug, EventAllowed, IntegrationSent, JiraIntegration, UserIntegration } from "@tr/models"
import { getData } from "@tr/utils"
import { ApiPaths, ApiService } from "./api.service"
import { FirebaseService } from "./firebase.service"

@Injectable({
  providedIn: "root",
})
export class IntegrationsService {
  constructor(
    private api: ApiService,
    private firebaseService: FirebaseService,
  ) {}

  public static jiraIssueLink(host: string, issueKey: string): string {
    return `${host}/browse/${issueKey}`
  }

  public async getData(companyId: string): Promise<UserIntegration[]> {
    return this.api.get(ApiPaths.integrations, { companyId })
  }

  public async delete(id: string): Promise<void> {
    await this.api.delete(ApiPaths.integrations, id)
  }

  public async add(integration: UserIntegration): Promise<UserIntegration> {
    if (integration.jira) {
      const passwordHash = await this.getIntegrationToken(integration.jira)
      delete integration.jira.password
      integration.jira.passwordHash = passwordHash
    }

    const integrationResponse = await this.api
      .post<UserIntegration, { id: string }>(ApiPaths.integrations, integration)
    integration._id = integrationResponse.id
    return integration
  }

  public async update(id: string, integration: UserIntegration): Promise<void> {
    if (integration.jira) {
      const passwordHash = await this.getIntegrationToken(integration.jira)
      delete integration.jira.password
      integration.jira.passwordHash = passwordHash
    }

    await this.api.put(`${ApiPaths.integrations}/${id}`, integration)
  }

  public async getById(id: string, integration?: UserIntegration): Promise<UserIntegration> {
    if (!integration) {
      integration = await this.api.get(`${ApiPaths.integrations}/${id}`)
    }

    if (integration.jira && integration.jira.passwordHash) {
      const jiraCredentials = await this.getIntegrationCredentials(integration.jira.passwordHash)
      integration.jira.password = jiraCredentials.password
      delete integration.jira.passwordHash
    }

    return integration
  }

  public getSentIntegrationByIdCollection(company: string, project: string, bugId: string, integrationId: string): AngularFirestoreDocument {
    return this.getSentIntegrationCollection(company, project, bugId)
      .doc(integrationId)
  }

  public getSentIntegrationCollection(company: string, project: string, bugId: string): AngularFirestoreCollection {
    return this.firebaseService
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Bugs")
      .doc(bugId)
      .collection("sentIntegrations")
  }

  public async getSentIntegrationsBy(company: string, project: string, bugId: string): Promise<IntegrationSent[]> {
    return getData<IntegrationSent>(
      await this.getSentIntegrationCollection(company, project, bugId).ref.get(),
    )
  }

  public async getIntegrationCredentials(token: string): Promise<JiraIntegration> {
    const getIntegrationCredentialsFromToken = firebase.functions().httpsCallable("getIntegrationCredentialsFromToken")
    const credentials = await getIntegrationCredentialsFromToken(token)
    return credentials.data
  }

  public async getIntegrationToken(jiraIntegration: JiraIntegration): Promise<string> {
    const getIntegrationTokenFromCredentials = firebase.functions().httpsCallable("getIntegrationTokenFromCredentials")
    const tokenData = await getIntegrationTokenFromCredentials(jiraIntegration)
    return tokenData.data
  }

  public async sendEventToIntegration(bug: Bug, eventType: EventAllowed, integrationIdsSelected: string[]): Promise<void> {
    const sendIntegrationWebhookBugEvent = firebase.functions().httpsCallable("sendIntegrationWebhookBugEvent")
    await sendIntegrationWebhookBugEvent({ bug, eventType, integrationIdsSelected })
  }
}
